import { useAccess } from '../../hooks/useAccess'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeftIcon, CubeIcon } from '@heroicons/react/24/outline'
import { jobProjectApi } from '../../services/jobProjectApi'
import { listStatusBadgeClass, opt } from '../../lib/optimizationStyles'

export default function ProjectHistory() {
  const { jobId } = useParams<{ jobId: string }>()
  const navigate = useNavigate()
  const access = useAccess()
  const rights = access.resource('optimization')

  const { data: project, isLoading, error } = useQuery({
    queryKey: ['project-history', jobId],
    queryFn: () => jobProjectApi.getProjectRuns(jobId!),
    enabled: !!jobId && rights.read,
  })

  if (!rights.read && !access.isLoading) return <p role="alert">Accès à l’historique non autorisé.</p>

  if (isLoading) {
    return (
      <div className={opt.page}>
        <div className={opt.content}>
          <div className="text-center py-12">
            <div className={opt.spinner} />
            <p className={`mt-4 ${opt.loadingText}`}>Chargement de l'historique...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className={opt.page}>
        <div className={opt.content}>
          <button type="button" onClick={() => navigate('/optimization')} className={opt.backBtn}>
            <ChevronLeftIcon className="w-4 h-4" />
            Retour aux projets
          </button>
          <div className={opt.alertErrorBox}>
            <p className="font-semibold mb-2">Erreur</p>
            <p>{error instanceof Error ? error.message : "L'historique n'a pas pu être chargé"}</p>
          </div>
        </div>
      </div>
    )
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Brouillon',
      queued: 'En attente',
      running: 'En cours',
      completed: 'Complété',
      failed: 'Échoué',
      cancelled: 'Annulé',
    }
    return labels[status] || status
  }

  return (
    <div className={opt.page}>
      <div className={opt.pageHeader}>
        <div className={opt.headerInnerMd}>
          <button type="button" onClick={() => navigate('/optimization')} className={opt.backBtn}>
            <ChevronLeftIcon className="w-4 h-4" />
            Retour aux projets
          </button>
          <h1 className={`${opt.h1} mb-2`}>Historique du Projet</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {project.reference} - {project.name}
          </p>
        </div>
      </div>

      <div className={opt.content}>
        <div className="space-y-4">
          <div className={opt.cardP6}>
            <h2 className={opt.h3Lg}>Informations du Projet</h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className={opt.dt}>Référence</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{project.reference}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={opt.dt}>Nom</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{project.name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={opt.dt}>Statut</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{project.status}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={opt.dt}>Créé le</dt>
                <dd className="font-medium text-gray-900 dark:text-white">
                  {new Date(project.created_at).toLocaleDateString('fr-FR')}
                </dd>
              </div>
            </dl>
          </div>

          <div className={opt.cardP6}>
            <h2 className={opt.h3Lg}>Historique des Optimisations</h2>
            {project.runs.length === 0 ? (
              <p className={opt.textMuted}>Aucune optimisation pour ce projet</p>
            ) : (
              <div className="mt-4 space-y-3">
                {project.runs.map((run) => (
                  <div
                    key={run.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                    onClick={() => navigate(`/optimization/${run.id}${rights.write ? '' : '/viewer'}`)}
                  >
                    <div className="flex items-center gap-4">
                      <CubeIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            Version {run.plan_version} · {run.input?.measurement_system === 'imperial' ? 'Impérial' : 'Métrique'}
                          </span>
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${listStatusBadgeClass(run.status)}`}
                          >
                            {getStatusLabel(run.status)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {new Date(run.requested_at).toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                        <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                          {run.publication
                            ? <>Diffusion : {run.publication.status} · {run.publication.artifact_count} artefact(s){run.publication.published_at ? ` · ${new Date(run.publication.published_at).toLocaleDateString('fr-FR')}` : ''}</>
                            : <>Diffusion : non demandée · 0 artefact</>}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {run.output && (
                        <div className="text-sm">
                          <div className="text-gray-600 dark:text-gray-400">
                            Vol: {((run.output.fill_rate_volume ?? 0) * 100).toFixed(1)}%
                          </div>
                          <div className="text-gray-600 dark:text-gray-400">
                            Score: {((run.output.global_score ?? 0) * 100).toFixed(1)}/100
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
